(() => {
  if (window.HugfigAddonDialog) return;

  const configurations = {
    keychain: { type: 'keychain', noun: 'keychain', title: 'Add a keychain to carry your HugFig.', dataSelector: '[data-hf-addons-data]' },
    figurine: { type: 'figurine_base', noun: 'base', title: 'Add a base to display your HugFig.', dataSelector: '[data-hf-base-addons-data]' }
  };
  const configuration = (productType) => configurations[productType] || null;
  const money = (cents) => `$${(Number(cents || 0) / 100).toFixed(2).replace(/\.00$/, '')}`;
  const safeURL = (value) => typeof value === 'string' && /^(https:|\/\/|blob:|data:image)/.test(value) ? value : '';
  const parseJSON = (element) => {
    try { return JSON.parse(element?.textContent || 'null'); } catch (_) { return null; }
  };
  function imageURL(source, width) {
    if (!safeURL(source)) return '';
    if (!/(cdn\.shopify\.com|\/cdn\/shop\/)/i.test(source)) return source;
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (_) { return ''; }
  }
  function imageSources(product, variant) {
    const candidate = variant?.featured_image || product?.featured_image || product?.images?.[0];
    if (!candidate) return { low: '', high: '' };
    if (typeof candidate === 'object') {
      const source = candidate.src || candidate.high || candidate.low || '';
      return { low: safeURL(candidate.low) || imageURL(source, 240), high: safeURL(candidate.high) || imageURL(source, 900) };
    }
    return { low: imageURL(candidate, 240), high: imageURL(candidate, 900) };
  }

  /** Shared selection UI only. The caller retains its own order submission. */
  class SharedAddonDialog {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-hf-addon-dialog]');
      this.container = root.querySelector('[data-hf-addons]');
      this.confirm = root.querySelector('[data-hf-addon-confirm]');
      this.pending = null;
      this.ownsScrollLock = false;
      this.confirm.addEventListener('click', () => {
        if (this.selectedAddon) this.finish('selected', 'confirm', this.selectedAddon);
      });
      root.querySelector('[data-hf-addon-skip]').addEventListener('click', () => this.finish('skipped', 'skip'));
      root.querySelector('[data-hf-addon-close]').addEventListener('click', () => this.finish('skipped', 'close'));
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.finish('cancelled', 'backdrop');
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.finish('cancelled', 'escape');
      });
      this.dialog.addEventListener('close', () => this.complete());
    }

    track(name, data) {
      // Analytics failure must never prevent a customer selecting or skipping.
      try { this.options.track?.(name, data, { availableStyles: this.availableStyles }); } catch (_) { /* Caller owns analytics delivery. */ }
    }

    render() {
      const config = this.config;
      const product = parseJSON(this.root.querySelector(config.dataSelector));
      this.root.querySelector('[data-hf-addon-title]').textContent = config.title;
      this.container.setAttribute('aria-label', `${config.noun === 'base' ? 'Base' : 'Keychain'} styles — choose one`);
      const variants = (product?.variants || []).filter((variant) => variant.available !== false && variant.id).slice(0, 4);
      this.availableStyles = [...new Set(variants.map((variant) => (variant.title === 'Default Title' ? product.title : variant.title) || variant.options?.filter(Boolean).join(' / ') || product.title || config.noun))].join('|');
      const count = this.root.querySelector('[data-hf-addon-count]');
      count.hidden = variants.length < 4;
      count.textContent = `${variants.length} styles · Scroll to see all`;
      this.container.replaceChildren(...variants.map((variant) => {
        const title = (variant.title === 'Default Title' ? product.title : variant.title) || variant.options?.filter(Boolean).join(' / ') || product.title || config.noun;
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'hf-rb-addon-option'; button.dataset.variantId = variant.id; button.setAttribute('aria-pressed', 'false');
        const media = document.createElement('span'); media.className = 'hf-rb-addon-option__media';
        const placeholder = document.createElement('span'); placeholder.className = 'hf-rb-addon-option__placeholder'; placeholder.textContent = 'Image unavailable';
        const sources = imageSources(product, variant);
        if (sources.high || sources.low) {
          const image = document.createElement('img');
          image.src = sources.high || sources.low; image.alt = ''; image.width = 104; image.height = 104;
          image.loading = 'lazy'; image.decoding = 'async'; image.fetchPriority = 'low';
          image.addEventListener('error', () => media.replaceChildren(placeholder), { once: true });
          media.append(image);
        } else media.append(placeholder);
        const check = document.createElement('span'); check.className = 'hf-rb-addon-option__check'; check.setAttribute('aria-hidden', 'true'); check.textContent = '✓';
        const body = document.createElement('span'); body.className = 'hf-rb-addon-option__body';
        const name = document.createElement('span'); name.className = 'hf-rb-addon-option__title'; name.textContent = title; body.append(name);
        if (typeof variant.short_description === 'string' && variant.short_description.trim()) {
          const description = document.createElement('span'); description.className = 'hf-rb-addon-option__description'; description.textContent = variant.short_description.trim(); body.append(description);
        }
        const pricing = document.createElement('span'); pricing.className = 'hf-rb-addon-option__pricing';
        const price = document.createElement('span'); price.className = 'hf-rb-addon-option__price'; price.textContent = `+${money(variant.price)}`; pricing.append(price);
        if (typeof variant.badge === 'string' && variant.badge.trim()) {
          const badge = document.createElement('span'); badge.className = 'hf-rb-addon-option__badge'; badge.textContent = variant.badge.trim(); pricing.append(badge);
        }
        body.append(pricing);
        button.append(check, media, body);
        button.addEventListener('click', () => {
          for (const item of this.container.querySelectorAll('.hf-rb-addon-option')) { item.classList.remove('is-selected'); item.setAttribute('aria-pressed', 'false'); }
          button.classList.add('is-selected'); button.setAttribute('aria-pressed', 'true');
          this.selectedAddon = { id: variant.id, title, price: variant.price, type: config.type };
          this.confirm.disabled = false; this.confirm.textContent = `Add & continue · +${money(variant.price)}`;
          this.root.querySelector('[data-hf-addon-status]').textContent = `${title} selected. Add-on price ${money(variant.price)}.`;
          if (config.type === 'keychain') this.track('keychain_addon_select', { placement: 'modal', keychain_style: title, variant_id: variant.id, price: variant.price });
        });
        return button;
      }));
      if (!variants.length) {
        const empty = document.createElement('p'); empty.className = 'hf-rb-addon-dialog__empty'; empty.textContent = `No ${config.noun} styles are available right now. You can continue without an add-on.`; this.container.append(empty);
      }
      return variants.length;
    }

    lockScroll() {
      if (document.body.classList.contains('hf-rb-dialog-open')) return;
      this.scrollY = window.scrollY;
      this.previousBodyTop = document.body.style.top;
      this.ownsScrollLock = true;
      document.documentElement.classList.add('hf-rb-dialog-open');
      document.body.classList.add('hf-rb-dialog-open');
      document.body.style.top = `-${this.scrollY}px`;
    }

    unlockScroll() {
      if (!this.ownsScrollLock) return;
      this.ownsScrollLock = false;
      document.documentElement.classList.remove('hf-rb-dialog-open');
      document.body.classList.remove('hf-rb-dialog-open');
      document.body.style.top = this.previousBodyTop;
      window.scrollTo(0, this.scrollY);
    }

    /** @returns {Promise<{outcome: string, reason: string, addon: object|null}>} */
    open(options) {
      if (this.pending) return Promise.resolve({ outcome: 'cancelled', reason: 'busy', addon: null });
      this.config = configuration(options.productType);
      if (!this.config) return Promise.resolve({ outcome: 'skipped', reason: 'unknown-product-type', addon: null });
      this.options = options;
      this.trigger = options.trigger || document.activeElement;
      this.selectedAddon = null;
      this.result = null;
      const count = this.render();
      this.confirm.disabled = true;
      this.confirm.textContent = `Select a ${this.config.noun} to continue`;
      this.root.querySelector('[data-hf-addon-status]').textContent = '';
      return new Promise((resolve, reject) => {
        this.pending = resolve;
        try {
          this.lockScroll();
          this.dialog.showModal();
          this.container.scrollTop = 0;
          if (this.config.type === 'keychain') this.track('keychain_addon_view', { placement: 'modal', available_keychain_styles: count });
        } catch (error) {
          this.pending = null;
          this.unlockScroll();
          reject(error);
        }
      });
    }

    finish(outcome, reason, addon = null) {
      if (!this.pending || this.result) return;
      this.result = { outcome, reason, addon };
      this.dialog.close();
    }

    complete() {
      const resolve = this.pending;
      if (!resolve) return;
      this.pending = null;
      this.unlockScroll();
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      resolve(this.result || { outcome: 'cancelled', reason: 'dismissed', addon: null });
    }
  }

  let instance = null;
  window.HugfigAddonDialog = {
    configuration,
    /**
     * @param {{productType: string, trigger?: HTMLElement, track?: Function}} options
     * track receives (name, legacyData, {availableStyles}); metadata is for the
     * caller's existing analytics adapter and is never automatically emitted.
     * @returns {Promise<{outcome: string, reason: string, addon: object|null}>}
     */
    open(options = {}) {
      const root = document.querySelector('[data-hf-shared-addons]');
      if (!root) return Promise.reject(new Error('Add-on options are temporarily unavailable. Please try again.'));
      if (!instance || instance.root !== root) instance = new SharedAddonDialog(root);
      return instance.open(options);
    }
  };
})();
